import React, { useState } from "react";
import { Camera, CheckCircle2, AlertTriangle, ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const LEGEND = [["bg-white border border-border text-navy", "Not visited"], ["bg-crimson text-white", "Not answered"], ["bg-emerald-600 text-white", "Answered"],
  ["bg-violet-600 text-white", "Marked for review"], ["bg-violet-600 text-white ring-2 ring-emerald-400", "Answered & marked"]];

export function Legend({ counts }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      {LEGEND.map(([c, l], i) => <div key={l} className="flex items-center gap-2"><span className={`h-7 w-7 rounded-md flex items-center justify-center font-bold ${c}`}>{counts ? counts[i] : ""}</span>{l}</div>)}
    </div>
  );
}

function CameraCheck({ p }) {
  const faceOk = p.ai !== "ready" || p.live.faces === 1;
  return (
    <div className="bg-navy rounded-xl p-4 text-white" data-testid="ts-camera-check">
      <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
        <video ref={p.videoRef} muted playsInline className="w-full h-full object-cover -scale-x-100" data-testid="ts-camera-preview" />
        {p.camera !== "on" && <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 text-sm gap-2"><Camera size={28} />Camera is off</div>}
      </div>
      {p.camera === "denied" && <p className="text-xs text-red-300 mt-3" data-testid="ts-camera-denied">Camera permission was blocked. Allow camera access in your browser settings and try again.</p>}
      {p.camera !== "on" ? <Button className="w-full mt-3 viriks-amber text-white" onClick={p.enable} disabled={p.camera === "starting"} data-testid="ts-enable-camera">{p.camera === "starting" ? "Starting camera…" : "Turn on camera"}</Button> : (
        <div className="mt-3 space-y-1.5 text-xs">
          <div className="flex items-center gap-2 text-emerald-300"><CheckCircle2 size={14} />Camera is on</div>
          {p.ai === "loading" && <div className="flex items-center gap-2 text-white/70"><Loader2 size={14} className="animate-spin" />Loading face monitoring…</div>}
          {p.ai === "ready" && <div className={`flex items-center gap-2 ${faceOk ? "text-emerald-300" : "text-amber-300"}`} data-testid="ts-face-status">{faceOk ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}{p.live.faces === 1 ? "Face detected" : p.live.faces > 1 ? "More than one face — sit alone" : "Face not visible — sit in front of the camera"}</div>}
          {p.ai === "unavailable" && <div className="flex items-center gap-2 text-amber-300"><AlertTriangle size={14} />Face monitoring could not load; photos and tab monitoring stay on.</div>}
        </div>
      )}
    </div>
  );
}

export function Instructions({ test, proctor, onStart }) {
  const [agree, setAgree] = useState(false);
  const faceOk = proctor.ai === "unavailable" || (proctor.ai === "ready" && proctor.live.faces === 1);
  const ready = agree && proctor.camera === "on" && faceOk;
  return (
    <div className="min-h-screen bg-muted/40" data-testid="ts-instructions">
      <div className="bg-navy text-white px-6 py-4"><div className="max-w-6xl mx-auto flex items-center justify-between"><span className="font-heading font-bold">{test.title}</span><span className="text-sm text-white/70">{test.duration} mins · {test.question_count} Qs · {test.total_marks} marks</span></div></div>
      <div className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="bg-white border border-border rounded-xl p-6 text-sm text-navy/80 space-y-5">
          <div><h2 className="font-heading font-bold text-navy text-lg">General Instructions</h2>
            <ol className="list-decimal pl-5 mt-3 space-y-2">
              <li>The clock is set on the server. The countdown timer at the top shows the time left; the test submits automatically when it reaches zero.</li>
              <li>The question palette on the right shows the status of each question:</li>
            </ol>
            <div className="mt-3 ml-5"><Legend /></div>
          </div>
          <div><h3 className="font-semibold text-navy">Answering</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1.5">
              <li>Click an option to select it. Click <b>Clear Response</b> to deselect.</li>
              <li>For written questions, type your answer in the box. It is checked by an evaluator after the test.</li>
              <li><b>Save & Next</b> saves the answer and moves on. <b>Mark for Review & Next</b> flags the question to revisit.</li>
              <li>Marking scheme: as shown on each question{test.negative_marking ? `; ${test.negative_marking} mark deducted for each wrong objective answer` : ""}.</li>
            </ul>
          </div>
          <div className="border border-crimson/30 bg-crimson/5 rounded-lg p-4"><h3 className="font-semibold text-crimson flex items-center gap-2"><ShieldAlert size={16} />Proctoring rules — the test ends on the first violation</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1.5">
              <li>Your camera must stay on for the whole test. A photo is saved every 30 seconds.</li>
              <li>The test is <b>auto-submitted immediately</b> if your face is not visible, another person appears, you look away from the screen, or you switch tab/window.</li>
              <li>A photo is captured at the moment of any violation and reviewed by VIRIKS.</li>
            </ul>
          </div>
          {test.instructions && <div><h3 className="font-semibold text-navy">Test instructions</h3><p className="mt-2 whitespace-pre-line">{test.instructions}</p></div>}
          <label className="flex items-start gap-3 border-t border-border pt-5 cursor-pointer"><Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="ts-declaration" /><span>I have read the instructions. I agree to camera monitoring and understand the test will be auto-submitted on any violation.</span></label>
        </div>
        <div className="space-y-4">
          <CameraCheck p={proctor} />
          <Button className="w-full h-12 viriks-amber text-white font-semibold" disabled={!ready} onClick={onStart} data-testid="ts-begin-btn">I am ready to begin</Button>
          {!ready && <p className="text-xs text-muted-foreground text-center">Turn on the camera, keep your face in view and accept the declaration.</p>}
        </div>
      </div>
    </div>
  );
}
